import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection, getDoc, onSnapshot, doc, updateDoc
} from 'firebase/firestore';
import { PencilIcon } from '@heroicons/react/outline';

// Initialize Firestore
const db = getFirestore();
const auth = getAuth();

const GlobalMarketValue = () => {
  const [valueChains, setValueChains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');

  // New states for user-input values per enterprise
  const [inputValues, setInputValues] = useState({}); // { chainId: value }
  const [globalMarketValues, setGlobalMarketValues] = useState({}); // { chainId: value }

  // State for the input modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    chainId: null,
    valueType: null, // 'inputValue' or 'globalMarketValue'
    currentValue: '',
  });

  // Fetch authenticated user details
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, user => {
      if (user) {
        setCurrentUser(user.uid);
        // We don't need to fetch the user's name for this component.
        // Setting the UID is enough to trigger the data fetch.
      } else {
        setCurrentUser(null);
      }
      // The loading state is now managed by the data fetching hook.
      // This was the primary issue causing the blank page.
    });
    return () => unsubscribeAuth();
  }, []);

  // Fetch value chains
  useEffect(() => {
    if (!currentUser) return;

    // Only set loading to true when we start fetching data for a user.
    // This prevents the loading state from being turned off prematurely by the auth check.
    setIsLoading(true); 

    const userDocRef = doc(db, "users", currentUser);
    // This collection reference is correct for fetching all enterprises ('kibalos')
    const valueChainsCollectionRef = collection(userDocRef, "kibalos");

    const unsubscribe = onSnapshot(valueChainsCollectionRef, async (snapshot) => {
      const initialInputValues = {};
      const initialGlobalMarketValues = {};
      const chainsData = await Promise.all(snapshot.docs.map(async (kibaloDoc) => {
        const data = kibaloDoc.data();

        // Calculate Total Cost (Input Cost / Year)
        const establishmentCost = data.establishmentCostPerYear || 0;
        const depreciationCost = data.depreciationTable?.reduce((acc, item) => acc + (parseFloat(item.depreciationAmount) || 0), 0) || 0;
        const maintenanceCost = data.maintenanceTable?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
        const interestCost = data.interestTable?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
        const variableCost = data.variableCosts?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
        
        const totalCost = establishmentCost + depreciationCost + maintenanceCost + interestCost + variableCost;

        // Calculate Gross Revenue
        const grossRevenue = data.grossRevenue?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;

        // Calculate Gross Profit (Profit / Year)
        const grossProfit = grossRevenue - totalCost;

        // Calculate Profit / Million Invested (Ugx)
        const profitPerMillion = totalCost !== 0 ? (grossProfit / totalCost) * 1000000 : 0;

        // Load existing saved values from Firestore document
        initialInputValues[kibaloDoc.id] = data.globalInputValue || 0;
        initialGlobalMarketValues[kibaloDoc.id] = data.globalMarketValue || 0;

        return {
          id: kibaloDoc.id,
          name: data.name,
          profitPerMillion: isFinite(profitPerMillion) ? profitPerMillion : 0,
        };
      }));

      // Sort by profitPerMillion in descending order
      const sortedChains = chainsData.sort((a, b) => {
        const rankA = a.profitPerMillion;
        const rankB = b.profitPerMillion;
        if (!isFinite(rankB)) return -1;
        if (!isFinite(rankA)) return 1;
        return rankB - rankA;
      });

      setValueChains(sortedChains);
      // Set the initial state for the input fields from the loaded data
      setInputValues(initialInputValues);
      setGlobalMarketValues(initialGlobalMarketValues);

      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching value chains:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Handle input change for Input Value
  const handleInputValueChange = (chainId, value) => {
    setInputValues(prev => ({ ...prev, [chainId]: parseFloat(value) || 0 }));
  };

  // Handle input change for Global Market
  const handleGlobalMarketValueChange = (chainId, value) => {
    setGlobalMarketValues(prev => ({ ...prev, [chainId]: parseFloat(value) || 0 }));
  };

  // Calculate BILLION
  const calculateBillion = (chainId, profitPerMillion) => {
    const inputValue = inputValues[chainId] || 0;
    const globalMarket = globalMarketValues[chainId] || 0;
    // The formula is (Input Value * Global Market * Profit / Million Invested (Ugx)) / 1,000,000,000
    return (inputValue * globalMarket * (profitPerMillion || 0)) / 1_000_000_000;
  };

  // Filter and Rank value chains based on search term and calculated BILLION value
  const rankedValueChains = useMemo(() => {
    const filtered = valueChains.filter(chain =>
      !searchTerm || (chain.name && chain.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const withBillionValue = filtered.map(chain => ({
      ...chain,
      billionValue: calculateBillion(chain.id, chain.profitPerMillion)
    }));

    return withBillionValue.sort((a, b) => b.billionValue - a.billionValue);
  }, [valueChains, searchTerm, inputValues, globalMarketValues]);

  // --- Modal Handlers ---

  const openModal = (chainId, valueType, currentValue) => {
    setModalState({
      isOpen: true,
      chainId,
      valueType,
      currentValue: currentValue || '',
    });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, chainId: null, valueType: null, currentValue: '' });
  };

  const handleModalValueChange = (e) => {
    setModalState(prev => ({ ...prev, currentValue: e.target.value }));
  };

  const handleModalSave = async () => {
    const { chainId, valueType, currentValue } = modalState;
    const numericValue = parseFloat(currentValue) || 0;

    // Firestore document reference for the specific enterprise being edited
    const kibaloRef = doc(db, `users/${currentUser}/kibalos/${chainId}`);

    if (valueType === 'inputValue') {
      handleInputValueChange(chainId, numericValue);
      try {
        await updateDoc(kibaloRef, { globalInputValue: numericValue });
      } catch (error) {
        console.error("Error saving Input Value:", error);
      }
    } else if (valueType === 'globalMarketValue') {
      handleGlobalMarketValueChange(chainId, numericValue);
      try {
        await updateDoc(kibaloRef, { globalMarketValue: numericValue });
      } catch (error) {
        console.error("Error saving Global Market Value:", error);
      }
    }
    closeModal();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">GLOBAL MARKET VALUE ANALYSIS</h1>
        {/* No "Create New Enterprise" button on this page */}
      </div>
      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by Enterprise Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        />
      </div>

      {isLoading && (
        <div className="text-center p-6">
          <p>Loading data...</p>
        </div>
      )}

      {!isLoading && (
      <>
      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto"> {/* Added overflow-x-auto for responsiveness */}
        <table className="min-w-full border border-gray-300">
          <thead className="bg-blue-500 text-white">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium">Rank</th>
              <th className="px-6 py-3 text-left text-sm font-medium">Enterprise Name</th>
              <th className="px-6 py-3 text-left text-sm font-medium">Import Value</th>
              <th className="px-6 py-3 text-left text-sm font-medium">Global Market</th>
              <th className="px-6 py-3 text-left text-sm font-medium">Profit / Million Invested (Ugx)</th>
              <th className="px-6 py-3 text-left text-sm font-medium">Ranker</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rankedValueChains.map((chain, index) => (
              <tr key={chain.id} className="hover:bg-gray-100">
                <td className="px-6 py-4 text-sm text-gray-800 border border-gray-300">{index + 1}</td>
                <td className="px-6 py-4 text-sm text-gray-800 border border-gray-300">{chain.name}</td>
                <td className="px-6 py-4 text-sm text-gray-600 border border-gray-300">
                  <div className="flex items-center justify-end space-x-2">
                    <span>{(inputValues[chain.id] || 0).toLocaleString()}</span>
                    <button onClick={() => openModal(chain.id, 'inputValue', inputValues[chain.id])} className="p-1 text-blue-600 rounded-full hover:bg-blue-100 focus:outline-none">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 border border-gray-300">
                  <div className="flex items-center justify-end space-x-2">
                    <span>{(globalMarketValues[chain.id] || 0).toLocaleString()}</span>
                    <button onClick={() => openModal(chain.id, 'globalMarketValue', globalMarketValues[chain.id])} className="p-1 text-blue-600 rounded-full hover:bg-blue-100 focus:outline-none">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 border border-gray-300">
                  {chain.profitPerMillion !== undefined ? chain.profitPerMillion.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : 'N/A'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-800 border border-gray-300">
                  {chain.billionValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
      
      {/* Input Modal */}
      {modalState.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">
              Enter {modalState.valueType === 'inputValue' ? 'Input Value' : 'Global Market Value'}
            </h2>
            <input
              type="number"
              value={modalState.currentValue}
              onChange={handleModalValueChange}
              className="p-2 border border-gray-300 rounded w-full mb-4"
              placeholder="Enter value..."
              min="0"
              autoFocus
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSave}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalMarketValue;